/**
 * 캐릭터 선택 화면.
 *
 * 첫 접속은 마찰이 0이어야 한다(DESIGN 1.1 — 10초 안에 전투).
 * 그래서 계정이 없으면 슬롯 화면을 건너뛰고 곧장 이름 입력으로 간다.
 * 두 번째부터는 고를 수 있어야 한다 — 이어서 할지, 새로 시작할지, 지울지.
 */
import { $, esc } from '../core/state.js';
import { levelLabel } from '../core/level-label.js';
import { josa, withJosa } from '../world/josa.js';
import { askConfirm } from './dialog.js';
import { hideWorldRecap } from './world-recap.js';
import { deleteCharacter } from './character-delete.js';

const MAX_SLOTS = 3;
const KLASS_KO = { melee: '근접', ranged: '원거리', assassin: '암살자' };

let onEnter = null; // (charId|null, name, klass) => Promise
let createTransition = null;
let slotRequest = null;
let renderedToken = null;
let slotGeneration = 0;

/** 결산 카드에서는 첫 입장 전에 직업을 고를 수 있도록 최신 슬롯의 선택 칸으로 보낸다. */
export async function enterCharacter(charId) {
  const token = localStorage.getItem('unige_token');
  if (!token || !charId) return;
  let refreshed = false;
  while (localStorage.getItem('unige_token') === token) {
    const pending = slotRequest;
    if (pending?.token === token) { await pending.promise; continue; }
    if (renderedToken === token) break;
    if (refreshed) break;
    refreshed = true;
    await loadSlots();
  }
  if (localStorage.getItem('unige_token') !== token) { hideWorldRecap(); return; }
  if (renderedToken !== token) {
    hideWorldRecap(); $('#err').textContent = '캐릭터 목록을 다시 불러온 뒤 입장해 달라.'; return;
  }
  const slots = $('#slots');
  const button = [...slots.querySelectorAll('[data-enter]')].find((item) => item.dataset.enter === charId);
  const choice = [...slots.querySelectorAll('[data-klass-for]')].find((item) => item.dataset.klassFor === charId);
  hideWorldRecap();
  if (!button) { $('#err').textContent = '캐릭터 목록을 다시 확인한 뒤 입장해 달라.'; return; }
  const target = choice || button;
  target.scrollIntoView?.({ block: 'nearest' });
  target.focus?.({ preventScroll: true });
}

export function initSelect(enterFn) {
  onEnter = enterFn;
  watchErr();
  refreshSelect();
}

/**
 * R203(레드팀 D2-16) — 입장 실패(「이미 쓰이는 이름이다」)·세계 종료 안내·삭제 실패가 `#err` 에 들어오면 **보이는 자리로 옮긴다.**
 * 전에는 오류 칸이 읽을거리·소개 아래라 폰(360×640·412×915)에서는 화면 밖에 찍혀, 입장 판이 잠깐 떴다 사라지고 버튼만 돌아왔다 —
 * 「눌렀는데 아무 일도 없다」. 이제 칸은 누른 버튼 바로 아래(index.html)이고, 글이 들어오면 그 자리까지 스크롤한다.
 * 목록을 다시 그리는 동안(세계 종료 뒤 목록 읽기) 칸이 밀려나면 그리고 나서 한 번 더 옮긴다.
 */
let errShownAt = -1e9;
function revealErr() {
  const el = $('#err');
  if (!el || !el.textContent.trim() || $('#start')?.style.display === 'none') return;
  el.scrollIntoView?.({ block: 'nearest' });
}
function revealErrSoon() {
  if (performance.now() - errShownAt < 5000) requestAnimationFrame(revealErr);
}
function watchErr() {
  const el = $('#err');
  if (!el || typeof MutationObserver !== 'function') return;
  new MutationObserver(() => {
    if (!el.textContent.trim()) return;
    errShownAt = performance.now();
    requestAnimationFrame(revealErr);
  }).observe(el, { childList: true, characterData: true, subtree: true });
}

/** Google 복구/전환 뒤 새 account의 슬롯을 즉시 다시 읽는다. */
export function refreshSelect() {
  const token = localStorage.getItem('unige_token');
  if (!token) { showCreate(false); return Promise.resolve(); }
  return loadSlots();
}

/** 시작 버튼이 현재 새 세계 직업 변경을 선택했는지 main.js가 읽는 값. */
export function createStartOptions(selectedKlass) {
  const available = !!createTransition?.available;
  const same = available && selectedKlass === createTransition.klass;
  return { classChange: available && !same };
}

async function loadSlots() {
  const token = localStorage.getItem('unige_token');
  const generation = ++slotGeneration;
  renderedToken = null;
  if (!token) { showCreate(false); return; }
  show('slots');
  $('#slots').innerHTML = '<div class="slothead">캐릭터 목록</div><div class="slotnote">최신 캐릭터 목록을 불러오는 중…</div>';
  const promise = loadSlotsForToken(token, generation);
  slotRequest = { token, promise };
  try { await promise; } finally { if (slotRequest?.promise === promise) slotRequest = null; }
}

async function loadSlotsForToken(token, generation) {
  try {
    // 계정 열쇠는 URL·접속 로그에 남기지 않는다. 서버의 전용 헤더 계약과 한 쌍이다.
    // 묶음 AA(DESIGN 14.431) — index.html 부트 스크립트가 모듈과 나란히 받아 둔 목록(같은 열쇠일 때 한 번만)
    const pre = globalThis.__unigeChars; globalThis.__unigeChars = null;
    const r = (pre?.token === token && await pre.res) || await fetch('/api/chars', { headers: { 'x-unige-token': token } });
    if (!r.ok) throw new Error(`캐릭터 목록을 불러오지 못했다.`);
    // Google 복구·계정 전환 중 이전 요청이 늦게 끝나도 새 계정의 슬롯을 덮지 않는다.
    const j = await r.json();
    if (localStorage.getItem('unige_token') !== token || generation !== slotGeneration) return;
    const list = j.chars || [];
    renderedToken = token;
    if (!list.length) { showCreate(false, j.worldTransition); return; }
    renderSlots(list, j.worldTransition);
  } catch (e) {
    if (localStorage.getItem('unige_token') !== token || generation !== slotGeneration) return;
    renderedToken = null;
    /**
     * **못 불러온 것과 없는 것은 다르다.**
     *
     * 전에는 네트워크가 실패해도 `showCreate(false)` 로 보냈다. 계정이 있는
     * 사람에게는 **캐릭터가 통째로 사라진 화면**이 뜬다. 그 상태에서 새로 만들면
     * 슬롯(최대 3)을 하나 더 먹고, 원래 캐릭터는 그대로 남아 있으니 되돌릴 수도 없다.
     * 조용한 실패가 데이터 손실처럼 보이는 전형이다.
     */
    show('slots');
    $('#slots').innerHTML = `<div class="slothead">캐릭터 목록</div>`
      + `<div class="slotnote">${esc(e.message || '연결에 실패했다.')}<br>`
      + `캐릭터 기록은 유지된다. 잠시 뒤 다시 불러와 달라.</div>`
      + `<button id="retryslots" style="width:100%;padding:12px;margin-top:6px">다시 불러오기</button>`;
    $('#retryslots').onclick = loadSlots;
  }
}

function show(which) {
  $('#slots').style.display = which === 'slots' ? 'block' : 'none';
  $('#create').style.display = which === 'create' ? 'block' : 'none';
}

function renderSlots(list, transition = null) {
  show('slots');
  // UNI-Q130 — 캐릭터는 세계를 넘어 남는다(제자리 초기화). 새 세계에 아직 입장하지 않았으면 초기화된 몸 하나의 직업을 1회 바꿀 수 있다.
  const changeNote = transition?.available && list.some((c) => c.classChangeReady)
    ? `<div class="slotnote">새 세계에 돌아왔다. 캐릭터는 이름 그대로 1레벨로 초기화됐다. 직업을 바꾸려면 몸 옆에서 고른 뒤 입장한다 — 이번 세계에서 1회.</div>`
    : '';
  const rows = list.map((c) => `
    <div class="slot">
      <div class="slotinfo">
        <b>${(c.star ?? c.admin) ? '<span class="adminmark">★</span> ' : (c.operator ? '<span class="adminmark">관리</span> ' : '')}${esc(c.name)}</b>
        <span>${c.guild ? `<i class="guildmark" style="--guild:${esc(c.guild.color)}" title="${esc(c.guild.name)}">${esc(c.guild.symbol)}</i>` : ''}${KLASS_KO[c.klass] || c.klass} · Lv.${levelLabel(c.level)} · ${c.kills}처치 · ${c.deaths}사망</span>
      </div>
      ${c.classChangeReady ? `<select data-klass-for="${c.id}" aria-label="${esc(c.name)} 직업" style="margin-right:4px">
        ${Object.entries(KLASS_KO).map(([k, ko]) => `<option value="${k}"${k === c.klass ? ' selected' : ''}>${ko}${k === c.klass ? ' (지금)' : ''}</option>`).join('')}
      </select>` : ''}
      <button class="gold" data-enter="${c.id}" data-klass="${esc(c.klass)}">입장</button>
      ${c.admin
        ? '<button class="del" disabled title="계정 관리 권한의 기준 캐릭터라 삭제할 수 없다">보호</button>'
        : `<button class="del" data-del="${c.id}" data-name="${esc(c.name)}" hidden
          data-lost="Lv.${levelLabel(c.level)} · ${c.kills}처치" title="되돌릴 수 없다">삭제</button>`}
    </div>`).join('');
  const empty = MAX_SLOTS - list.length;
  const news = empty > 0
    ? `<button id="newchar" style="width:100%;padding:12px;margin-top:6px">＋ 새 캐릭터 (${empty}칸 남음)</button>`
    : `<div class="slotnote">캐릭터 슬롯이 가득 찼다. 새 캐릭터를 만들려면 기존 캐릭터를 삭제해야 한다.</div>`;
  /**
   * 묶음 AA(DESIGN 14.431) — 돌아온 사람의 첫 누름은 「마지막 캐릭터로 이어하기」 하나다(목록은 최근 순 — `/api/chars` updated_at DESC).
   * 직업을 바꿀 수 있는 몸(새 세계 1회)은 목록의 직업 칸을 봐야 하므로 큰 버튼을 두지 않는다. 삭제는 [입장] 옆에 같은 크기로 서 있지 않고
   * 「캐릭터 삭제…」를 한 번 눌러야 드러난다(되돌릴 수 없는 동작을 한 겹 안쪽에).
   */
  const last = list[0];
  const resume = last && !last.classChangeReady
    ? `<button id="resumechar" class="gold" data-enter="${last.id}" data-klass="${esc(last.klass)}">이어하기 — ${esc(last.name)} <small>${KLASS_KO[last.klass] || last.klass} · Lv.${levelLabel(last.level)}</small></button>`
    : '';
  const delToggle = list.some((c) => !c.admin) ? `<button id="delmode" class="linkish" type="button">캐릭터 삭제…</button>` : '';
  $('#slots').innerHTML = `${resume}<div class="slothead">캐릭터 선택</div>${changeNote}${rows}${news}${delToggle}`;
  const dm = $('#delmode');
  if (dm) dm.onclick = () => {
    const on = dm.dataset.on !== '1';
    dm.dataset.on = on ? '1' : '';
    dm.textContent = on ? '삭제 닫기' : '캐릭터 삭제…';
    $('#slots').querySelectorAll('[data-del]').forEach((b) => { b.hidden = !on; });
  };

  $('#slots').querySelectorAll('[data-enter]').forEach((b) => {
    b.onclick = () => {
      const pick = $('#slots').querySelector(`[data-klass-for="${b.dataset.enter}"]`)?.value;
      const change = !!pick && pick !== b.dataset.klass;
      if (!change) { onEnter(b.dataset.enter, null, null, false); return; }
      // R166 — 되돌릴 수 없는 결정(세계당 1회)은 도트 확인 창이 묻는다. 누른 뒤의 입장 버튼 활성화는 onEnter 가 맡는다
      askConfirm(`직업을 ${withJosa(KLASS_KO[pick] || pick, '로')} 바꾼다. 이번 세계에서 1회만 쓸 수 있다.`,
        () => onEnter(b.dataset.enter, null, pick, true), { ok: '바꾸고 입장' });
    };
  });
  $('#slots').querySelectorAll('[data-del]').forEach((b) => {
    b.onclick = async () => {
      /**
       * 확인 문구에 **무엇을 잃는지** 적는다. 이름만 보여주면 어느 캐릭터인지는
       * 알아도 얼마짜리인지는 모른다 — 이 게임에서 가장 되돌릴 수 없는 동작이다.
       */
      // R166 — 이 게임에서 가장 되돌릴 수 없는 동작이다. 브라우저 경고창이 아니라 도트 확인 창이 묻고, 「취소」에 먼저 손이 간다
      const yes = await askConfirm(`「${b.dataset.name}」(${b.dataset.lost})${josa(b.dataset.name, '을')} 지운다.\n`
        + '캐릭터와 가방·착용 장비가 삭제되며 복구할 수 없다. 계정 창고는 이 세계 동안 유지된다.', null, { ok: '지운다', danger: true });
      if (!yes) return;
      b.disabled = true;
      const oldLabel = b.textContent;
      const token = localStorage.getItem('unige_token');
      b.textContent = '정리 중…';
      try {
        await deleteCharacter({ token, charId: b.dataset.del }, {
          isCurrent: () => b.isConnected && localStorage.getItem('unige_token') === token,
          onPending: () => { b.textContent = '저장 확인 중…'; },
        });
      } catch (e) {
        b.disabled = false;
        b.textContent = oldLabel;
        $('#err').textContent = e.message || '캐릭터를 삭제하지 못했다. 잠시 뒤 다시 시도해 달라.';
        return;
      }
      loadSlots();
    };
  });
  const nb = $('#newchar');
  if (nb) nb.onclick = () => showCreate(true, null);
  revealErrSoon();
}

function showCreate(canGoBack, transition = null) {
  createTransition = transition?.available ? transition : null;
  show('create');
  $('#back').style.display = canGoBack ? 'block' : 'none';
  const name = $('#nm');
  // 전환이 닫힌 계정(한 세계 이상 쉬었다)도 지난 이름을 채워 둔다 — 고정은 아니다(서버 `restoringLegacy` 와 같은 `available` 기준)
  const lastName = transition?.nickname ?? '';
  name.value = createTransition?.nickname ?? lastName;
  name.readOnly = !!createTransition?.nickname;
  const klass = createTransition?.klass ?? (lastName ? transition?.klass : null);
  if (klass) {
    document.querySelectorAll('.klass button').forEach((b) => {
      const on = b.dataset.k === klass;
      b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  const notice = $('#world-transition');
  if (notice) {
    notice.hidden = !createTransition && !lastName;
    notice.textContent = createTransition
      ? `새 세계에 돌아왔다. 닉네임 「${createTransition.nickname}」${josa(createTransition.nickname, '은')} 유지된다. 직업을 바꾸면 이번 세계에서 1회만 사용할 수 있다.`
      : lastName ? `지난 이름 「${lastName}」${josa(lastName, '을')} 채워 두었다. 다른 이름으로 바꿔도 된다.` : '';
  }
  // 모바일에서 자동 포커스는 소프트 키보드를 즉시 올려 **시작 화면 절반을 덮는다.**
  // 로고도 클래스 선택도 안 보이는 채로 시작하게 된다. 좁은 화면에서는 안 한다.
  if (innerWidth > 760) $('#nm').focus();
  revealErrSoon();
}

export function backToSelect() {
  $('#start').style.removeProperty('display');
  $('#game').style.display = 'none';
  $('#err').textContent = '';
  hideWorldRecap();
  const go = $('#go');
  // R212(레드팀 D2-22) — 같은 버튼이 처음엔 「게임 시작」, 돌아오면 「입장」이었다. 한 이름으로(실패 복구 main.js 와 같은 이름)
  go.disabled = false; go.textContent = '게임 시작';
  createTransition = null;
  $('#nm').readOnly = false;
  const notice = $('#world-transition'); if (notice) { notice.hidden = true; notice.textContent = ''; }
  loadSlots();
}
